import React, { useState, useEffect } from 'react';
import { useParams } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { apiRequest, getQueryFn, queryClient } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';
import { Scope1Form } from '../components/Scope1Form';
import { Scope1MeasuredForm } from '../components/Scope1MeasuredForm';
import { Scope1Scenario1Form } from '../components/scope1/Scope1Scenario1Form';
import { Scope1Scenario2Form } from '../components/scope1/Scope1Scenario2Form';
import { Scope2Form } from '../components/Scope2Form';
import { Scope3Form } from '../components/Scope3Form';
import { Scope1UploadForm } from '../components/Scope1UploadForm';
import { Scope2UploadForm } from '../components/Scope2UploadForm';
import { Scope3UploadForm } from '../components/Scope3UploadForm';
import { Scope1EntriesTable } from '../components/scope1/Scope1EntriesTable';
import { Scope2EntriesTable } from '../components/scope2/Scope2EntriesTable';
import { Scope3EntriesTable } from '../components/scope3/Scope3EntriesTable';
import { GasCalculator } from '../components/gas-calculator';

interface EmissionEntry {
  id: number;
  scope: string;
  activity_type: string;
  quantity: number;
  unit: string;
  co2_equivalent: number;
  status: string;
  is_estimated: boolean;
}

const scopeInfo = {
  scope1: {
    title: 'Scope 1 - Direct Emissions',
    description: 'Direct environmental emissions from sources owned or controlled by your organization',
    color: 'green',
  },
  scope2: {
    title: 'Scope 2 - Indirect Energy Emissions',
    description: 'Indirect emissions from purchased electricity, steam, heating, and cooling',
    color: 'blue',
  },
  scope3: {
    title: 'Scope 3 - Other Indirect Emissions',
    description: 'All other indirect emissions in your value chain',
    color: 'purple',
  },
};

export interface Scope1FormProps {
  onSubmit: (formData: any) => void;
  isSubmitting: boolean;
}

export default function ScopeEntry() {
  const params = useParams<{ scopeType: string }>();
  const scopeType = params.scopeType as keyof typeof scopeInfo;
  const { toast } = useToast();
  const [selectedTab, setSelectedTab] = useState<'measured' | 'estimation' | 'upload' | 'vehicle-details' | 'existing' | 'calculator'>('upload');
  const [scenarioTab, setScenarioTab] = useState<'vehicles' | 'generators'>('vehicles');

  useEffect(() => {
    const feather = (window as any).feather as { replace: (opts?: Record<string, unknown>) => void } | undefined;
    feather?.replace();
  }, []);

  const { data: companies } = useQuery({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
  });

  // Company selection (industry is read-only from company)
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const selectedCompany = companies?.find((c: any) => c.id === selectedCompanyId) ?? null;
  
  // Year filter for existing entries
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useEffect(() => {
    if (!selectedCompanyId && companies && companies.length > 0) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  // Industry is derived from selectedCompany.industry

  const { data: emissionEntries, isLoading } = useQuery({
    queryKey: [
      selectedCompany?.id
        ? `/api/emissions?company_id=${selectedCompany.id}${scopeType ? `&scope=${scopeType}` : ''}${selectedYear ? `&year=${selectedYear}` : ''}`
        : null,
    ],
    queryFn: getQueryFn(),
    enabled: !!selectedCompany?.id,
  });

  const createEmissionMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', '/api/emissions', data);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Creation failed');
      }
      return res.json();
    },
    onSuccess: () => {
      if (selectedCompany?.id) {
        queryClient.invalidateQueries({
          queryKey: [
            `/api/emissions?company_id=${selectedCompany.id}${scopeType ? `&scope=${scopeType}` : ''}`,
          ],
        });
      }
      toast({
        title: 'Entry Created',
        description: 'Emission entry has been saved successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Creation Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (formData: any) => {
    if (!selectedCompany) {
      toast({
        title: 'No Company Profile',
        description: 'Please create a company profile first.',
        variant: 'destructive',
      });
      return;
    }

    if (scopeType === 'scope1') {
      // Use estimation wizard endpoint for Scope 1
      (async () => {
        try {
          const payload = { ...formData, company_id: selectedCompany.id };
          const res = await apiRequest('POST', '/api/ghg/scope1', payload);
          await res.json();
          queryClient.invalidateQueries({
            queryKey: [
              `/api/emissions?company_id=${selectedCompany.id}&scope=${scopeType}`,
            ],
          });
          toast({ title: 'Estimation Saved', description: 'Scope 1 estimation saved.' });
        } catch (e: any) {
          toast({ title: 'Estimation Failed', description: e.message, variant: 'destructive' });
        }
      })();
    } else {
      // Fallback to generic emissions create for other scopes
      createEmissionMutation.mutate({
        ...formData,
        company_id: selectedCompany.id,
      });
    }
  };

  const renderScopeForm = () => {
    switch (scopeType) {
      case 'scope1':
        return (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2 border-b">
              {[
                { key: 'measured', label: 'Measured Activity' },
                { key: 'estimation', label: 'Estimation Wizard' },
                { key: 'upload', label: 'File Upload' },
                { key: 'existing', label: 'Existing Entries' },
                { key: 'calculator', label: 'Gas Calculator' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                    (selectedTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800')
                  }`}
                  onClick={() => setSelectedTab(tab.key as any)}
                  type="button"
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {selectedTab === 'measured' && (
              <div>
                <Scope1MeasuredForm
                  onSubmit={(data) =>
                    createEmissionMutation.mutate({ ...data, company_id: selectedCompany?.id })
                  }
                  isSubmitting={createEmissionMutation.isPending}
                  industry={selectedCompany?.industry || selectedCompany?.industry_name}
                  scopeLabel={'Scope 1'}
                  selectedCompanyId={selectedCompany?.id}
                />
              </div>
            )}

            {selectedTab === 'estimation' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 border-b">
                  {[{key:'vehicles',label:'Scenario 1: Vehicles (fuel unknown)'},{key:'generators',label:'Scenario 2: Generators (spend)'}].map(tab => (
                    <button
                      key={tab.key}
                      className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                        scenarioTab === (tab.key as any) ? 'border-green-600 text-green-700' : 'border-transparent text-gray-600 hover:text-gray-800'
                      }`}
                      onClick={() => setScenarioTab(tab.key as any)}
                      type="button"
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {scenarioTab === 'vehicles' && (
                  <Scope1Scenario1Form
                    companyId={selectedCompany?.id}
                    onSaved={() => {
                      if (selectedCompany?.id) {
                        queryClient.invalidateQueries({
                          queryKey: [`/api/emissions?company_id=${selectedCompany.id}&scope=${scopeType}`],
                        });
                      }
                      toast({ title: 'Saved', description: 'Scenario 1 estimation saved.' });
                    }}
                  />
                )}

                {scenarioTab === 'generators' && (
                  <Scope1Scenario2Form
                    companyId={selectedCompany?.id}
                    onSaved={() => {
                      if (selectedCompany?.id) {
                        queryClient.invalidateQueries({
                          queryKey: [`/api/emissions?company_id=${selectedCompany.id}&scope=${scopeType}`],
                        });
                      }
                      toast({ title: 'Saved', description: 'Scenario 2 estimation saved.' });
                    }}
                  />
                )}
              </div>
            )}

            {selectedTab === 'upload' && (
              <div>
                <Scope1UploadForm
                  companyId={selectedCompany?.id}
                  onUploaded={() => {
                    if (selectedCompany?.id) {
                      queryClient.invalidateQueries({
                        queryKey: [
                          `/api/emissions?company_id=${selectedCompany.id}${scopeType ? `&scope=${scopeType}` : ''}`,
                        ],
                      });
                    }
                  }}
                />
              </div>
            )}

            {selectedTab === 'existing' && (
              <div>
                {/* Year Filter */}
                <div className="mb-4">
                  <label htmlFor="year-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Year
                  </label>
                  <select
                    id="year-filter"
                    value={selectedYear || ''}
                    onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Years</option>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <Scope1EntriesTable 
                  entries={emissionEntries || []} 
                  isLoading={isLoading} 
                />
              </div>
            )}

            {selectedTab === 'calculator' && <GasCalculator />}
          </div>
        );
      case 'scope2':
        return (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2 border-b">
              {[
                { key: 'measured', label: 'Measured Activity' },
                { key: 'upload', label: 'File Upload' },
                { key: 'existing', label: 'Existing Entries' },
                { key: 'calculator', label: 'Gas Calculator' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                    (selectedTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800')
                  }`}
                  onClick={() => setSelectedTab(tab.key as any)}
                  type="button"
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {selectedTab === 'measured' && (
              <div>
                <Scope2Form 
                  onSubmit={handleSubmit} 
                  isSubmitting={createEmissionMutation.isPending}
                  selectedCompanyId={selectedCompany?.id}
                />
              </div>
            )}

            {selectedTab === 'upload' && (
              <div>
                <Scope2UploadForm
                  companyId={selectedCompany?.id}
                  onUploaded={() => {
                    if (selectedCompany?.id) {
                      queryClient.invalidateQueries({
                        queryKey: [
                          `/api/emissions?company_id=${selectedCompany.id}${scopeType ? `&scope=${scopeType}` : ''}`,
                        ],
                      });
                    }
                  }}
                />
              </div>
            )}

            {selectedTab === 'existing' && (
              <div>
                {/* Year Filter */}
                <div className="mb-4">
                  <label htmlFor="year-filter-scope2" className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Year
                  </label>
                  <select
                    id="year-filter-scope2"
                    value={selectedYear || ''}
                    onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Years</option>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <Scope2EntriesTable 
                  entries={emissionEntries || []} 
                  isLoading={isLoading} 
                />
              </div>
            )}

            {selectedTab === 'calculator' && <GasCalculator />}
          </div>
        );
      case 'scope3':
        return (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2 border-b">
              {[
                { key: 'upload', label: 'File Upload' },
                { key: 'existing', label: 'Existing Entries' },
                { key: 'calculator', label: 'Gas Calculator' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                    (selectedTab === tab.key ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-600 hover:text-gray-800')
                  }`}
                  onClick={() => setSelectedTab(tab.key as any)}
                  type="button"
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {selectedTab === 'upload' && (
              <div>
                <Scope3UploadForm
                  companyId={selectedCompany?.id}
                  onUploaded={() => {
                    if (selectedCompany?.id) {
                      queryClient.invalidateQueries({
                        queryKey: [
                          `/api/emissions?company_id=${selectedCompany.id}${scopeType ? `&scope=${scopeType}` : ''}`,
                        ],
                      });
                    }
                  }}
                />
              </div>
            )}

            {selectedTab === 'existing' && (
              <div>
                {/* Year Filter */}
                <div className="mb-4">
                  <label htmlFor="year-filter-scope3" className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Year
                  </label>
                  <select
                    id="year-filter-scope3"
                    value={selectedYear || ''}
                    onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Years</option>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <Scope3EntriesTable 
                  entries={emissionEntries || []} 
                  isLoading={isLoading} 
                />
              </div>
            )}

            {selectedTab === 'calculator' && <GasCalculator />}
          </div>
        );
      default:
        return <p>Invalid scope selected.</p>;
    }
  };

  const scopeConfig = scopeInfo[scopeType];

  if (!scopeConfig) {
    // Render a not found page or a message
    return <div>Invalid scope</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <h1 className={`text-xl font-bold text-${scopeConfig.color}-700`}>{scopeConfig.title}</h1>
              <p className="text-sm text-gray-600 mt-1">{scopeConfig.description}</p>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Company & Industry</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Select Company</label>
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={selectedCompanyId ?? ''}
                      onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
                    >
                      {(companies || []).map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Industry</label>
                    <div className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-800">
                      {selectedCompany?.industry_name || '—'}
                    </div>
                  </div>
                </div>

                <div className="mt-2">
                  <h3 className="text-lg font-semibold mb-2">New Entry</h3>
                  {!selectedCompany ? (
                    <div className="text-gray-500">Please select a company to continue.</div>
                  ) : (
                    renderScopeForm()
                  )}
                </div>
              </CardContent>
            </Card>

          </div>
        </main>
      </div>
    </div>
  );
}
